/* eslint-disable consistent-return */
import { useState, useMemo } from 'react';
import { Typography, Grid } from '@mui/material';
import { useQueryClient } from 'react-query';
import { isURL } from 'validator';

// Our components
import Alert from 'components/Alert';
import CurrencyTextInput from 'components/Input/CurrencyTextInput';
import { PrimaryButton } from 'components/Button/Button';
import Dropdown from 'components/Dropdown/Dropdown';
import GetFullAddressPartialForm from 'components/Forms/addressRelated/GetFullAddressPartialForm';
import Input from 'components/Input/TextInput';
import Loader from 'components/Loader';
import LoanInquirySuccessModal from 'components/Modal/LoanInquiryConfirmationModal';
import SoraTextField from 'components/Input/SoraTextField';
import { NUMBER, TEXT } from 'components/Input/Types';

// Our hooks 🪝
import useMutateCreateDeal from 'hooks/hubspot/useMutateCreateDeal';
import useMutateSaveNewLoanInquiry from 'hooks/newLoanInquiries/useMutateSaveNewLoanInquiry';
import useMutateUpdateLoanInquiry from 'hooks/newLoanInquiries/useMutateUpdatedLoanInquiry';
import useMutateSendGenericEmail from 'hooks/emails/useMutateSendGenericEmail';

// Our Query keys
import { ERROR_MESSAGE_DATA } from 'shared/query-keys';

// Utils
import {
	clearFormValues,
	dollarFormatter,
	isSubmissionReady
} from 'shared/utils';
import createDefaultCloseDate from 'shared/utils/hubspot/createDefaultCloseDate';
import formatCollection from 'shared/utils/formatting/currency/formatCollection';

// Our emails
import lendingEmailTemplate from 'emails/lendingEmailTemplate';

// Constants
import { BUSINESS, NEW_LOAN_OFFER_TYPE } from 'shared/constants';

function SmallBusinessLoanForm({
	advisorInfo,
	clientInfo,
	isClientNameProvided,
	financialData,
	loanType,
	loanRequestId
}) {
	const queryClient = useQueryClient();
	const updateLoanInquiry = useMutateUpdateLoanInquiry();

	// API Calls
	const saveLoanInquiry = useMutateSaveNewLoanInquiry();
	const { isLoading: savingLoanInquiry } = saveLoanInquiry;

	// Mutations
	const sendGenericEmail = useMutateSendGenericEmail();
	const createDeal = useMutateCreateDeal();

	// Advisor Info
	const { advisorName, advisorEmail, advisorCompany, advisorWebsite } =
		advisorInfo;

	// Client Info
	const { clientFullName, clientId, emailAddressValue, firstName, lastName } =
		clientInfo;

	// Pre-populated state items
	const [reasonForLoan, setReasonForLoan] = useState('');
	const [loanAmount, setLoanAmount] = useState('');
	const [companyName, setCompanyName] = useState('');
	const [companyType, setCompanyType] = useState('');
	const [companyWebsite, setCompanyWebsite] = useState('');

	const [selectedLoanType, setSelectedLoanType] = useState('');

	// Address related fields
	const [addressLine1, setAddressLine1] = useState('');
	const [addressLine2, setAddressLine2] = useState('');
	const [city, setCity] = useState('');
	const [state, setState] = useState('');
	const [zipCode, setZipCode] = useState('');

	const [revenueLastYear, setRevenueLastYear] = useState('');
	const [incomeLastYear, setIncomeLastYear] = useState('');
	const [revenue2YearsAgo, setRevenue2YearsAgo] = useState('');
	const [income2YearsAgo, setIncome2YearsAgo] = useState('');
	const [creditScore, setCreditScore] = useState('');

	const [additionalNotes, setAdditionalNotes] = useState('');
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

	const requiredFormValues = useMemo(
		() => [reasonForLoan, loanAmount, loanType],
		[reasonForLoan, loanAmount, loanType]
	);
	const isFormReady = useMemo(
		() => isClientNameProvided && isSubmissionReady(requiredFormValues),
		[isClientNameProvided, ...requiredFormValues]
	);
	const isValidWebsite = useMemo(
		() => isURL(companyWebsite),
		[companyWebsite]
	);

	const getCompanyWebsiteErrorMessage = useMemo(() => {
		if (companyWebsite === '') return 'Company website cannot be empty';
		if (!isValidWebsite) return 'Company website is not valid';
	}, [companyWebsite]);

	const sendFormData = () => {
		const sendToArray = ['lending@sorafinance.com'];
		const subject = `Commercial Loan Request - ${firstName} ${lastName}`;

		const emailTemplate = lendingEmailTemplate({
			advisorInfo,
			clientInfo,
			financialData,
			clientFullName,
			loanAmount,
			loanRelatedDetails: [
				`Reason for loan: ${reasonForLoan}`,
				`Company name: ${companyName}`,
				`Company type: ${companyType}`,
				`Company website: ${companyWebsite}`,
				`Loan type: ${selectedLoanType}`,
				`Revenue last year: ${dollarFormatter.format(
					+revenueLastYear
				)}`,
				`Income last year: ${dollarFormatter.format(+incomeLastYear)}`,
				`Revenue 2 years ago: ${dollarFormatter.format(
					+revenue2YearsAgo
				)}`,
				`Income 2 years ago: ${dollarFormatter.format(
					+income2YearsAgo
				)}`,
				`Credit score of guarantor(s): ${creditScore}`,
				`Company HQ location: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}`
			],
			additionalNotes,
			lendingTypeRequested: 'Commercial Loan'
		});

		sendGenericEmail.mutate(
			{
				firstName,
				lastName,
				subject,
				message: emailTemplate,
				sendToArray
			},
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setAddressLine1,
						setAddressLine2,
						setCity,
						setCompanyName,
						setCompanyType,
						setCompanyWebsite,
						setCreditScore,
						setIncome2YearsAgo,
						setIncomeLastYear,
						setLoanAmount,
						setSelectedLoanType,
						setReasonForLoan,
						setRevenue2YearsAgo,
						setRevenueLastYear,
						setState,
						setZipCode
					]);
				}
			}
		);
	};

	const submitNewLoanInquiry = () => {
		const newLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			companyName,
			companyType,
			companyWebsite,
			creditScore,
			emailAddressValue,
			firstName,
			income2YearsAgo,
			incomeLastYear,
			lastName,
			loanAmount,
			selectedLoanType,
			offerType: NEW_LOAN_OFFER_TYPE,
			reasonForLoan,
			revenue2YearsAgo,
			revenueLastYear,
			state,
			tradelineType: BUSINESS,
			zipCode
		};

		// Call mutation to update user's data
		saveLoanInquiry.mutate(newLoanInquiryData, {
			onSuccess: () => {
				setIsSuccessModalOpen(true);
			},
			onError: () => {
				// Set error message
				queryClient.setQueryData(
					ERROR_MESSAGE_DATA,
					'There was an error making your loan request.'
				);
			}
		});
	};

	const updateLoanRequest = () => {
		const updatedLoanInquiryData = {
			additionalNotes,
			addressLine1,
			addressLine2,
			city,
			clientId,
			companyName,
			companyType,
			companyWebsite,
			creditScore,
			emailAddressValue,
			firstName,
			income2YearsAgo,
			incomeLastYear,
			lastName,
			loanAmount,
			selectedLoanType,
			offerType: NEW_LOAN_OFFER_TYPE,
			reasonForLoan,
			revenue2YearsAgo,
			revenueLastYear,
			state,
			tradelineType: BUSINESS,
			zipCode
		};

		// Call mutation to update user's data
		updateLoanInquiry.mutate(
			{ loanRequestId, updatedLoanInquiryData },
			{
				onSuccess: () => {
					clearFormValues([
						setAdditionalNotes,
						setAddressLine1,
						setAddressLine2,
						setCity,
						setCompanyName,
						setCompanyType,
						setCompanyWebsite,
						setCreditScore,
						setIncome2YearsAgo,
						setIncomeLastYear,
						setLoanAmount,
						setSelectedLoanType,
						setReasonForLoan,
						setRevenue2YearsAgo,
						setRevenueLastYear,
						setState,
						setZipCode
					]);
				},
				onError: () => {
					// Set error message
					queryClient.setQueryData(
						ERROR_MESSAGE_DATA,
						'There was an error making your loan request.'
					);
				}
			}
		);
	};

	const { isLoading } = sendFormData;

	const currentURL = window.location.href;
	const atLoanRequestDetailsUrl = currentURL.includes('loan-request-details');

	const submitForm = (event) => {
		event.preventDefault();

		const closeDate = createDefaultCloseDate();
		const valuesToFormat = [
			{
				type: 'currency',
				value: loanAmount,
				label: 'Desired loan amount:'
			},
			{
				type: 'currency',
				value: revenueLastYear,
				label: 'Revenue from 2023'
			},
			{
				type: 'currency',
				value: incomeLastYear,
				label: 'Income from 2023'
			},
			{
				type: 'currency',
				value: revenue2YearsAgo,
				label: 'Revenue from 2022'
			},
			{
				type: 'currency',
				value: income2YearsAgo,
				label: 'Income from 2022'
			}
		];

		const formattedCollection = formatCollection(valuesToFormat);
		const advisorNotes = `Advisor's name: ${advisorName}
				Advisor's email: ${advisorEmail}
				Advisor's company: ${advisorCompany}
				Credit Score: ${creditScore}
				Reason for loan: ${reasonForLoan}
				${formattedCollection}
				Company Name: ${companyName}
				Company Type: ${companyType}
				Company Website: ${companyWebsite}
				Highest credit score of guarantor(s): ${creditScore}
				Loan Type: ${selectedLoanType}
				Company headquarters location: ${addressLine1} ${addressLine2} ${city}, ${state} ${zipCode}
				------------------------------------------------------------------------------------------------
				Advisor's notes: \n${additionalNotes}`;

		createDeal.mutate({
			advisorInfo: {
				advisorName,
				email: advisorEmail,
				companyName: advisorCompany,
				companyDomain: advisorWebsite
			},
			properties: {
				amount: loanAmount,
				dealname: `${advisorName} / ${clientFullName}`,
				dealtype: loanType,
				borrower: clientFullName,
				closedate: closeDate
			},
			noteInfo: {
				hs_note_body: advisorNotes
			}
		});

		// If we're at /loan-inquiry, perform the following
		if (!atLoanRequestDetailsUrl) {
			submitNewLoanInquiry();
			sendFormData();
		}
		// Else if we're at /loan-inquiry-details, perform the following
		else if (atLoanRequestDetailsUrl) {
			// Call new mutations here patching the new loan inquiry
			updateLoanRequest();
		}
	};

	// Handlers for mutation
	const { isError: sendFormDataError } = sendFormData;

	return (
		<>
			{!atLoanRequestDetailsUrl && (
				<Grid item xs={12}>
					<Typography variant="h2Gascogne">
						Commercial Loan
					</Typography>

					<Typography variant="body2">
						Fill in the information below to submit a loan request
						to Sora. You will be able to complete and review your
						loan request in the dashboard task bar.
					</Typography>
				</Grid>
			)}

			{isLoading || (savingLoanInquiry && <Loader />)}
			{!isLoading && !savingLoanInquiry && (
				<>
					{sendFormDataError && <Alert variant="error" />}

					{/* Modal shown on successful submission of  */}
					<LoanInquirySuccessModal isOpen={isSuccessModalOpen} />
					<>
						<Grid item xs={12} md={6}>
							{/* Reason for loan */}
							<Input
								id="reasonForLoan"
								label="Reason for loan"
								type={TEXT}
								name="Reason for loan"
								onChange={setReasonForLoan}
								value={reasonForLoan}
								required
								error={reasonForLoan === ''}
								helperText="Reason for loan cannot be empty"
								endAdornment="hidden"
								inputProps={{
									'data-test': 'reasonForLoan'
								}}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							{/* Desired Loan Amount */}
							<CurrencyTextInput
								label="Desired loan amount"
								value={loanAmount}
								onChange={setLoanAmount}
								type={NUMBER}
								required
								error={loanAmount === ''}
								helperText="Desired loan amount cannot be empty"
								inputProps={{
									'data-test': 'loanAmount'
								}}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							{/* Company name */}
							<Input
								id="companyName"
								label="Company name"
								type={TEXT}
								name="Company name"
								onChange={setCompanyName}
								value={companyName}
								endAdornment="hidden"
								inputProps={{
									'data-test': 'companyName'
								}}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<Dropdown
								items={['LLC', 'S-corp', 'C-corp', 'Other']}
								selected={companyType}
								onChange={setCompanyType}
								variant="outlined"
								label="Company type"
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<Input
								id="companyWebsite"
								label="Company Website"
								type={TEXT}
								onChange={setCompanyWebsite}
								value={companyWebsite}
								error={!isValidWebsite}
								helperText={getCompanyWebsiteErrorMessage}
								placeholder="companywebsite.com"
								inputProps={{
									'data-test': 'companyWebsite'
								}}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							{/* Last years revenue */}
							<CurrencyTextInput
								label={`Revenue from ${
									new Date().getFullYear() - 1
								}`}
								subLabel="Estimate is fine"
								value={revenueLastYear}
								onChange={setRevenueLastYear}
								type={NUMBER}
								inputProps={{
									'data-test': 'revenueLastYear'
								}}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							{/* Last years income */}
							<CurrencyTextInput
								label={`Income from ${
									new Date().getFullYear() - 1
								}`}
								subLabel="Estimate is fine"
								value={incomeLastYear}
								onChange={setIncomeLastYear}
								type={NUMBER}
								inputProps={{
									'data-test': 'incomeLastYear'
								}}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							{/* Revenue 2 years ago */}
							<CurrencyTextInput
								label={`Revenue from ${
									new Date().getFullYear() - 2
								}`}
								subLabel="Estimate is fine"
								value={revenue2YearsAgo}
								onChange={setRevenue2YearsAgo}
								type={NUMBER}
								inputProps={{
									'data-test': 'revenue2YearsAgo'
								}}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							{/* Net income 2 years ago */}
							<CurrencyTextInput
								label={`Income from ${
									new Date().getFullYear() - 2
								}`}
								subLabel="Estimate is fine"
								value={income2YearsAgo}
								onChange={setIncome2YearsAgo}
								type={NUMBER}
								inputProps={{
									'data-test': 'income2YearsAgo'
								}}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							{/* Credit Score */}
							<Input
								label="Highest credit score of guarantor(s)"
								value={creditScore}
								onChange={setCreditScore}
								type={NUMBER}
								inputProps={{
									'data-test': 'creditScore'
								}}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							{/* Loan Type */}
							<Dropdown
								items={[
									'SBA Loans',
									'Revolving Line of Credit',
									'Commercial Real Estate'
								]}
								selected={selectedLoanType}
								onChange={setSelectedLoanType}
								variant="outlined"
								label="Loan Type"
								required
							/>
						</Grid>

						<GetFullAddressPartialForm
							label="Company headquarters location"
							addressLine1={addressLine1}
							addressLine2={addressLine2}
							city={city}
							state={state}
							zipCode={zipCode}
							setAddressLine1={setAddressLine1}
							setAddressLine2={setAddressLine2}
							setCity={setCity}
							setState={setState}
							setZipCode={setZipCode}
						/>
						<Grid item xs={12}>
							<SoraTextField
								label="Additional Notes"
								value={additionalNotes}
								onChange={setAdditionalNotes}
							/>
						</Grid>
					</>
				</>
			)}

			<Grid item xs={12}>
				<PrimaryButton disabled={!isFormReady} onClick={submitForm}>
					Submit
				</PrimaryButton>
			</Grid>
		</>
	);
}

export default SmallBusinessLoanForm;
